import React from 'react';
import { useTranslation } from 'react-i18next';
import { TeamTable } from '@/features/team/components/TeamTable';
import { EditUserForm } from '@/features/team/components/EditUserForm';
import { useTeam, useTeamUser } from '@/features/team/hooks/useTeam';
import { TeamUserFormData } from '@/features/team/types/team.types';
import PageMeta from '@/components/common/PageMeta';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import { useSearchParams } from 'react-router-dom';

const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { teamUsers, loading, updateUser } = useTeam();

  const editUserId = searchParams.get('edit-user');
  const { user: editingUser } = useTeamUser(editUserId || '');

  const [, setSearchParams] = useSearchParams();

  const handleEditUser = (userId: string) => {
    setSearchParams(prev => {
      prev.set('edit-user', userId);
      return prev;
    });
  };

  const closeEditModal = () => {
    setSearchParams(prev => {
      prev.delete('edit-user');
      return prev;
    });
  };

  const handleSaveUser = async (formData: TeamUserFormData) => {
    if (!editUserId) return;

    try {
      await updateUser(editUserId, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: formData.role
      });
      closeEditModal();
    } catch (error) {
      throw error;
    }
  };

  return (
    <>
      <PageMeta
        title={t('team.title')}
        description={t('team.description')}
      />

      <div className="max-w-7xl">
        <PageBreadcrumb pageTitle="Team" />

        <div className="flex flex-col gap-6">
          <TeamTable
            teamUsers={teamUsers}
            loading={loading}
            onEditUser={handleEditUser}
          />
        </div>
      </div>

      <ModalWithSearchParam
        paramName="edit-user"
        maxWidth="600px"
        onClose={(hasUnsavedChanges) => {
          // Always close when X button is clicked or when explicitly requested
          if (hasUnsavedChanges !== true) {
            closeEditModal();
          }
        }}
      >
        {editingUser && (
          <EditUserForm
            user={editingUser}
            onSave={handleSaveUser}
            onCancel={closeEditModal}
          />
        )}
      </ModalWithSearchParam>
    </>
  );
};

export default TeamPage;
